import { Component, OnInit, Output, EventEmitter, Input, SimpleChanges } from '@angular/core';
import { CONSTANTS } from 'src/Core/Constant';
import { SevaProfileService } from 'src/app/Service/seva-profile.service';
import { ToastrService } from 'ngx-toastr';
import { GenericValidationsService } from 'src/app/Service/generic-validations.service';
import { FormGroup } from '@angular/forms';
import { TranslationService } from 'src/app/Service/translation.service';

@Component({
  selector: 'app-document',
  templateUrl: './document.component.html',
  styleUrls: ['./document.component.scss']
})
export class DocumentComponent implements OnInit {


   // Input property for preview modeag to determine behavior

   @Output() formGroupChange = new EventEmitter<FormGroup>();
   @Output() nextStep = new EventEmitter<void>();
   @Output() previous = new EventEmitter<void>();
   @Input() applicantData: any; // Accept data from parent component
   @Input() isPreviewMode: boolean = false; // Fl


 
 // API Endpoints
 private documentsEndpoint = 'FileHandler/GetAllDocuments';
 private documentsUploadEndpoint = 'FileHandler/UploadMultipleFiles';
 private documentsDeleteEndpoint = 'FileHandler/DeleteFileById?id=';


 // Button related properties
 DocumentType = CONSTANTS.DocumentType;
 documents: any[] = [];
 SaveMode = CONSTANTS.SAVE_MODE;
 showValidationErrors = false;
 isEditable: boolean = true; // Flag to track if the form is editable
 
 // Image preview properties
 showPreview = false;
 previewImageUrl = '';
 previewPosition = { x: 0, y: 0 };
 // File validation constants
 private readonly MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes
 private readonly ALLOWED_IMAGE_FORMATS = ['image/jpeg', 'image/jpg', 'image/png'];
 private readonly ALLOWED_DOCUMENT_FORMATS = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];



 constructor(
 public genericValidations: GenericValidationsService,
  private sevaProfileService: SevaProfileService,
  private toastr: ToastrService,
  private translation: TranslationService
 ) {}




 ngOnInit(): void {
  if (!this.isPreviewMode) {
  this.loadDocuments();
  }
 }




 ngOnChanges(changes: SimpleChanges): void {

  if (changes['applicantData'] && this.applicantData) {

       this.documents = Array.isArray(this.applicantData) ? this.applicantData : (this.applicantData?.Data.UserDocuments ?? this.applicantData?.Data.UserDocuments ?? []);
 
  }
}
// helper
getTranslation(key: string): string {
  return this.translation.getTranslation(key);
}




 onFileSelected(documentType: number, event: Event): void {
  const input = event.target as HTMLInputElement;
  if (!input.files || input.files.length === 0) {
   return;
  }
  const files = Array.from(input.files);
  
  // Validate each file
  const validFiles: File[] = [];
  for (const file of files) {
    this.showFileSizeInfo(file);
    if (this.validateFile(file, documentType)) {
      validFiles.push(file);
    }
  }
  
  if (validFiles.length > 0) {
    this.uploadDocuments(documentType, validFiles);
  }
  
  input.value = '';
 }

 /**
  * Validates file format and size based on document type
  */
 private validateFile(file: File, documentType: number): boolean {
   // Check file size (5MB limit for all documents)
   if (file.size > this.MAX_FILE_SIZE) {
     this.toastr.error(`File size must be less than 5MB. Current size: ${this.formatFileSize(file.size)}`);
     return false;
   }

   // Special validation for Passport size photograph (ProfilePic)
   if (documentType === CONSTANTS.DocumentType.ProfilePic) {
     if (!this.ALLOWED_IMAGE_FORMATS.includes(file.type)) {
       this.toastr.error('Passport size photograph must be in JPEG or PNG format only.');
       return false;
     }
   } else {
     // For all other documents, allow PDF, JPEG, PNG
     if (!this.ALLOWED_DOCUMENT_FORMATS.includes(file.type)) {
       this.toastr.error('Document must be in PDF, JPEG, or PNG format only.');
       return false;
     }
   }

   return true;
 }

 /**
  * Formats file size for display
  */
 private formatFileSize(bytes: number): string {
   if (bytes === 0) return '0 Bytes';
   const k = 1024;
   const sizes = ['Bytes', 'KB', 'MB', 'GB'];
   const i = Math.floor(Math.log(bytes) / Math.log(k));
   return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
 }

 /**
  * Gets allowed file extensions for display
  */
 getAllowedExtensions(documentType: number): string {
   if (documentType === CONSTANTS.DocumentType.ProfilePic) {
     return 'JPEG, PNG';
   }
   return 'PDF, JPEG, PNG';
 }

 /**
  * Gets maximum file size for display
  */
 getMaxFileSize(): string {
   return '5 MB';
 }

 /**
  * Shows file size information for user feedback
  */
 showFileSizeInfo(file: File): void {
   const fileSize = this.formatFileSize(file.size);
   const maxSize = this.getMaxFileSize();
   
   if (file.size > this.MAX_FILE_SIZE) {
     this.toastr.warning(`File size: ${fileSize} (exceeds ${maxSize} limit)`);
   } else {
    //  this.toastr.info(`File size: ${fileSize} (within ${maxSize} limit)`);
   }
 }

 private uploadDocuments(documentType: number, files: File[]): void {
  // Get document type name for toast message
  const documentTypeName = this.getDocumentTypeName(documentType);
  const fileNames = files.map(f => f.name).join(', ');
  
  // Show uploading toast (with a unique ID so we can clear it)
  const uploadingToast = this.toastr.info(
    this.getTranslation('documents.uploading') || `Uploading ${files.length} file(s)...`,
    this.getTranslation('documents.uploadingTitle') || 'Uploading',
    { 
      timeOut: 0, 
      closeButton: false,
      disableTimeOut: true,
      tapToDismiss: false
    }
  );
  const uploadingToastId = uploadingToast?.toastId;

  this.sevaProfileService
   .uploadMultipleFiles(documentType, files, this.documentsUploadEndpoint, { 
     applicationTypeId: CONSTANTS.ApplicationTypeId.SEVA_PROFIE 
   })
   .subscribe({
    next: (response: any) => {
      // Close uploading toast if it exists
      if (uploadingToastId) {
        this.toastr.remove(uploadingToastId);
      }
      
      // Show success toast
      const successMessage = this.getTranslation('success.documentUploaded') 
        || `Files uploaded successfully`;
      this.toastr.success(successMessage, this.getTranslation('success.title') || 'Success', {
        timeOut: 3000,
        progressBar: true,
        closeButton: true
      });
      
      this.loadDocuments();
      // Reset validation errors when document is uploaded
      this.showValidationErrors = false;
    },
    error: (err: any) => {
      // Close uploading toast if it exists
      if (uploadingToastId) {
        this.toastr.remove(uploadingToastId);
      }
      
      // Show error toast
      const errorMessage = this.getTranslation('error.uploadFailed') 
        || `Failed to upload files. Please try again.`;
      this.toastr.error(errorMessage, this.getTranslation('error.title') || 'Error', {
        timeOut: 5000,
        progressBar: true,
        closeButton: true
      });
      
      console.error('Upload failed', err);
    }
   });
 }

 private loadDocuments(): void {
  this.sevaProfileService
   .getAllDocuments(this.documentsEndpoint)
   .subscribe({
    next: (res: any) => {
      this.genericValidations.setProgressPercentage(res.Data.CompletionPercentage);

      // Store IsEditable flag from response
      this.isEditable = res.Data.IsEditable !== false; // Default to true if not provided

     this.documents = Array.isArray(res) ? res : (res?.Data.UserDocuments ?? res?.Data.UserDocuments ?? []);
    },
    error: (err) => console.error('Failed to load documents', err)
   });
 }

 getDocumentByType(documentType: number): any {
  return this.documents.find(doc => (doc.DocType ?? doc.documentType) === documentType);
 }

 /**
  * Gets all documents of a specific type (returns array for multiple documents)
  */
 getDocumentsByType(documentType: number): any[] {
  return this.documents.filter(doc => (doc.DocType ?? doc.documentType) === documentType);
 }

 /**
  * Gets document type name for display in toast messages
  */
 private getDocumentTypeName(documentType: number): string {
  const typeMap: { [key: number]: string } = {
    [CONSTANTS.DocumentType.ProfilePic]: this.getTranslation('documents.doc.passportPhoto') || 'Passport Photo',
    [CONSTANTS.DocumentType.AadharCard]: this.getTranslation('documents.doc.aadharCard') || 'Aadhaar Card',
    [CONSTANTS.DocumentType.PhotoIdProof]: this.getTranslation('documents.doc.photoIdProof') || 'Photo ID Proof',
    [CONSTANTS.DocumentType.PanCard]: this.getTranslation('documents.doc.panCard') || 'PAN Card',
    [CONSTANTS.DocumentType.Resume]: this.getTranslation('documents.doc.resume') || 'Resume',
    [CONSTANTS.DocumentType.SevaProfileDoc]: this.getTranslation('documents.doc.sevaProfileDoc') || 'Seva Profile Document',
    [CONSTANTS.DocumentType.VSDoc]: this.getTranslation('documents.doc.vsDocument') || 'VS Document',
    [CONSTANTS.DocumentType.Other]: this.getTranslation('documents.doc.other') || 'Other Document'
  };
  
  return typeMap[documentType] || this.getTranslation('documents.doc.other') || 'Document';
 }

 // Delete document method
 deleteDocument(documentId: number, documentName: string): void {
   // Safety check - ensure document exists and has valid ID
   if (!documentId || documentId <= 0) {
    this.toastr.warning(
      this.getTranslation('validation.requiredField') || 'Invalid document ID',
      this.getTranslation('validation.title') || 'Validation Error'
    );
     return;
   }

  if (!documentName) {
    documentName = this.getTranslation('documents.doc.other') || 'Document';
  }

  const confirmMessage = this.getTranslation('documents.deleteConfirm') 
    || `Are you sure you want to delete "${documentName}"?`;
  
  if (confirm(confirmMessage)) {
    // Show deleting toast (with a unique ID so we can clear it)
    const deletingToast = this.toastr.info(
      this.getTranslation('documents.deleting') || `Deleting ${documentName}...`,
      this.getTranslation('documents.deletingTitle') || 'Deleting',
      { 
        timeOut: 0, 
        closeButton: false,
        disableTimeOut: true,
        tapToDismiss: false
      }
    );
    const deletingToastId = deletingToast?.toastId;

    this.sevaProfileService.apiCall(
      `${this.documentsDeleteEndpoint}${documentId}`,
      'DELETE'
    ).subscribe({
       next: (response: any) => {
        // Close deleting toast if it exists
        if (deletingToastId) {
          this.toastr.remove(deletingToastId);
        }
        
        // Show success toast
        const successMessage = this.getTranslation('success.documentDeleted') 
          || `"${documentName}" deleted successfully`;
        this.toastr.success(successMessage, this.getTranslation('success.title') || 'Success', {
          timeOut: 3000,
          progressBar: true,
          closeButton: true
        });
        
        this.loadDocuments(); // Reload documents after deletion
        // Reset validation errors when document is deleted
        this.showValidationErrors = false;
       },
       error: (error: any) => {
        // Close deleting toast if it exists
        if (deletingToastId) {
          this.toastr.remove(deletingToastId);
        }
        
        // Show error toast
        const errorMessage = this.getTranslation('error.deleteFailed') 
          || `Failed to delete "${documentName}". Please try again.`;
        this.toastr.error(errorMessage, this.getTranslation('error.title') || 'Error', {
          timeOut: 5000,
          progressBar: true,
          closeButton: true
        });
        
        console.error('Delete failed:', error);
       }
    });
   }
 }

 // Button event handlers
 onSubmit(saveMode: number): void {
  if (saveMode === CONSTANTS.SAVE_MODE.SAVE) {
   // Validate required documents before proceeding
   if (this.validateRequiredDocuments()) {
     this.showValidationErrors = false;
     this.nextStep.emit();
    
   } else {
     this.showValidationErrors = true;
    this.toastr.warning(this.getTranslation('validation.completeAllFields'));
   }
  }
 }

 // Validate required documents
 private validateRequiredDocuments(): boolean {

   const profilePicDocs = this.getDocumentsByType(CONSTANTS.DocumentType.ProfilePic);
   const aadharCardDocs = this.getDocumentsByType(CONSTANTS.DocumentType.AadharCard);
   
   return profilePicDocs.length > 0 && aadharCardDocs.length > 0;
 }

 goToBack(): void {
  this.previous.emit();
 }

 // Image preview methods
 showImagePreview(event: MouseEvent, imageUrl: string): void {
   this.previewImageUrl = imageUrl;
   this.previewPosition = {
     x: event.clientX + 10,
     y: event.clientY + 10
   };
   this.showPreview = true;
 }

 hideImagePreview(): void {
   this.showPreview = false;
 }
}
